import { Stack } from '@mui/system'
import { useSearchParams } from 'react-router-dom'
import { createEventSet, getTokenInfo, saveToken } from 'services/api/tikTokApi'
import { useContext, useEffect, useLayoutEffect, useState } from 'react'
import useSnackbar from 'hooks/useSnackbar'
import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  Step,
  StepContent,
  Stepper,
  TextField,
  Typography,
} from '@material-ui/core'
import { Button } from '@hi/react-material'
import { ExpandLess, ExpandMore } from '@material-ui/icons'
import { getTenantId } from 'utils/token/token'
import { Header, NewEventBtn, NewEventSection, RadioLabel, SCRadioGroup, SCStepLabel } from './styles'
import RouterContext from '../../contexts/Router'
import mixpanel from 'mixpanel-browser'

function Callback() {
  const [searchParams] = useSearchParams()
  const { createSnackbar } = useSnackbar()
  const history = useContext(RouterContext)
  const tenantId = getTenantId()
  const [account, setAccount] = useState({
    accessToken: '',
    advertisers: [],
    eventSets: [],
    selectedEventSets: [],
    isLoading: true,
  })
  const [activeStep, setActiveStep] = useState(0)
  const [selectedAccount, setSelectedAccount] = useState({
    advertiserId: '',
    advertiserName: '',
    eventSetId: '',
    eventSetName: '',
    isValid: false,
  })
  const [newEventSet, setNewEventSet] = useState({ isLoading: false, isValid: false, name: '', errors: '' })

  const authCode = searchParams.get('auth_code')

  useLayoutEffect(() => {
    mixpanel.track_pageview(
      {page:  'Conexão TikTok Ads Callback'}, 
      {'event_name': 'Acesso - Conexão TikTok Ads'}
    )
  }, [])

  async function handleCallback(code, tenant) {
    const { status, data } = await getTokenInfo({ authCode: code, state: tenant })

    if (status === 'OK') {
      if (data.advertiser_info.length === 0) {
        createSnackbar(
          'Você ainda não possui nenhum anúncio nessa conta. Para conectá-la, crie um anúncio no seu TikTok Business.',
        )
      }

      setAccount({
        accessToken: data.access_token,
        advertisers: data.advertiser_info,
        eventSets: data.event_set_info,
        selectedEventSets: [],
        isLoading: false,
      })
    }
  }

  const handleSelectAdvertiser = (e) => {
    const advertiserId = e.target.value
    const isValid = advertiserId && selectedAccount.eventSetId
    const advertiser = account.advertisers.find((ad) => ad.id === advertiserId)
    const selectedEventSets = account.eventSets.find((sets) => sets.advertiserId === advertiserId).eventSets

    setAccount({ ...account, selectedEventSets })
    setSelectedAccount({ ...selectedAccount, advertiserId, advertiserName: advertiser.name, isValid })
  }

  const handleSelectEventSet = (e) => {
    const eventSetId = e.target.value
    const isValid = selectedAccount.advertiserId && eventSetId
    const eventSet = account.selectedEventSets.find((event) => event.event_set_id === eventSetId)

    setSelectedAccount({ ...selectedAccount, eventSetId, eventSetName: eventSet.name, isValid })
  }

  const handleConfirm = async () => {
    await saveToken({
      accessToken: account.accessToken,
      advertiserId: selectedAccount.advertiserId,
      advertiserName: selectedAccount.advertiserName,
      eventSetId: selectedAccount.eventSetId,
      eventSetName: selectedAccount.eventSetName,
      tenantId,
    }).then(() => {
      history.push('/accountsSelfConnection')
    })
  }

  const handleStep = () => {
    let nextStep = 0
    if (activeStep === 0) nextStep = 1
    setActiveStep(nextStep)
  }

  const handleChangeEvent = (e) => {
    const isValid = Boolean(e.target.value)
    setNewEventSet({ ...newEventSet, isValid, name: e.target.value })
  }

  const handleNewEvent = async () => {
    setNewEventSet({ ...newEventSet, isLoading: true })
    const { status, data } = await createEventSet({
      accessToken: account.accessToken,
      name: newEventSet.name,
      advertiserId: selectedAccount.advertiserId,
    })

    if (status === 'OK') {
      if (data?.code === 40000) {
        setNewEventSet({
          ...newEventSet,
          isLoading: false,
          errors: 'O nome do evento não pode ser igual a um já existente.',
        })
      } else {
        const {
          data: { event_set: eventSet },
        } = data
        setNewEventSet({ ...newEventSet, isLoading: false, errors: '', name: '' })
        setAccount({
          ...account,
          selectedEventSets: [
            ...account.selectedEventSets,
            { name: eventSet.name, event_set_id: eventSet.event_set_id },
          ],
        })
      }
    }
  }

  useEffect(() => {
    if (authCode) {
      handleCallback(authCode, tenantId)
    } else {
      createSnackbar('Não foi possível realizar a autenticação.')
    }
  }, [])

  return (
    <Stack direction="column" justifyContent="center" alignItems="flex-start" marginX={10} marginY={3}>
      <Header>
        <Typography variant="h3" style={{ fontSize: '24px' }}>
          Conectar TikTok Ads com a Hi Platform
        </Typography>
        <div>
          <Button onClick={() => history.push('/accountsSelfConnection')}>Cancelar</Button>
          <Button color="primary" variant="contained" disabled={!selectedAccount.isValid} onClick={handleConfirm}>
            Confirmar conexão
          </Button>
        </div>
      </Header>

      {!account.isLoading ? (
        <Box marginTop={5}>
          <Stepper orientation="vertical" activeStep={activeStep}>
            <Step>
              <SCStepLabel>
                Escolha a conta de anúncios do TikTok
                <IconButton size="small" onClick={handleStep} disabled={!selectedAccount.advertiserId}>
                  {activeStep === 0 ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </SCStepLabel>
              <StepContent>
                <FormControl>
                  <FormLabel id="advertiser-label">
                    Selecione abaixo a conta de anúncios do TikTok que deseja conectar com a Hi Platform.
                  </FormLabel>
                  <SCRadioGroup
                    name="advertiser"
                    aria-labelledby="advertiser-label"
                    value={selectedAccount.advertiserId}
                    color="primary"
                    onChange={handleSelectAdvertiser}
                  >
                    {account.advertisers.map((ad) => (
                      <RadioLabel
                        value={ad.id}
                        control={<Radio  data-testid={`radio-advertiser-${ad.id}`}/>}
                        label={
                          <>
                            {ad.name}
                            <span>advertiser ID : {ad.id}</span>
                          </>
                        }
                        key={ad.id}
                      />
                    ))}
                  </SCRadioGroup>
                </FormControl>
              </StepContent>
            </Step>
            <Step>
              <SCStepLabel>
                Selecione o conjunto de eventos do TikTok
                <IconButton size="small" data-testid='button-expand' onClick={handleStep} disabled={!selectedAccount.advertiserId}>
                  {activeStep === 1 ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </SCStepLabel>
              <StepContent>
                <FormControl>
                  <FormLabel>
                    Selecione abaixo o conjunto de eventos do TikTok que deseja conectar com a Hi Platform.
                  </FormLabel>
                  <SCRadioGroup
                    name="eventSet"
                    aria-labelledby="eventSet-label"
                    onChange={handleSelectEventSet}
                    value={selectedAccount.eventSetId}
                  >
                    {account.selectedEventSets.map((event) => (
                      <FormControlLabel
                        value={event.event_set_id}
                        control={<Radio data-testid={`radio-event-${event.event_set_id}`}/>}
                        label={event.name}
                        key={event.event_set_id}
                        data-testid='form-control-data-testid-event'
                      />
                    ))}
                  </SCRadioGroup>
                </FormControl>

                <Box marginTop={2}>
                  <Typography variant="body2" style={{ color: '#666', fontSize: '1rem' }}>
                    Para criar um novo conjunto de eventos, insira o nome do conjunto no campo abaixo e pressione o
                    botão.
                  </Typography>
                  <NewEventSection>
                    <TextField
                      name="new-eventSet"
                      label="Nome do conjunto de eventos"
                      variant="outlined"
                      size="small"
                      value={newEventSet.name}
                      onChange={handleChangeEvent}
                      error={newEventSet.errors}
                      helperText={newEventSet.errors}
                      fullWidth
                    />
                    <NewEventBtn
                      variant="outlined"
                      color="primary"
                      disabled={newEventSet.isLoading || !newEventSet.isValid}
                      onClick={handleNewEvent}
                    >
                      Criar conjunto
                    </NewEventBtn>
                  </NewEventSection>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      ) : (
        <Box display="flex" width="100%" height="100%" justifyContent="center" alignItems="center" marginY={8}>
          <CircularProgress />
        </Box>
      )}
    </Stack>
  )
}

export default Callback
